/**
 * ReportingTab — Charts + event reason table + recent events table (area version).
 *
 * Reuses existing V1 chart components:
 *   - HourlyLineAndBarChart (alarm activity by hour)
 *   - LineTrendingChart (weekly alarm trend)
 *
 * Displays event reason breakdown and recent events in dp-table format.
 * Area version shows "Device" column instead of "Location" (since we're on the area).
 */

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import HourlyLineAndBarChart from '../../../../../components/charts/HourlyLineAndBarChart.jsx';
import LineTrendingChart from '../../../../../components/charts/LineTrendingChart.jsx';
import { convertIsoToLocal } from '../../../../../libs.jsx';

const ReportingTab = ({
  hourData, weekData, reasonData, eventData,
  eventNotesActive, isReasonEditable, tzOffset,
  loading, onAddNote, areaId, areaName,
}) => {
  // Reason breakdown rows
  const reasonRows = useMemo(() => {
    if (!reasonData?.reasons) return [];
    return reasonData.reasons;
  }, [reasonData]);

  // Weekly chart data (formatted for LineTrendingChart)
  const weekChartProps = useMemo(() => ({
    lineData: [weekData?.events?.length > 0 ? weekData.events : []],
    lineColors: ['#0081C7'],
    lineLabels: ['Total Alarms'],
    axisLabels: weekData?.labels?.length > 0 ? weekData.labels : [],
  }), [weekData]);

  /** Build the correct profile link for a device based on manufacturer */
  const getDeviceLink = (evt) => {
    if (evt.manufacture_id === 4) {
      return `/profiles/palcare/${evt.device_id}`;
    }
    return `/profiles/inovonics/${evt.device_id}`;
  };

  return (
    <>
      {/* Report action + summary stats */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 'var(--space-2)', marginBottom: 'var(--space-3)' }}>
        <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-success-text)', fontWeight: 'var(--font-weight-semibold)' }}>
          {!isNaN(hourData?.avgValue) ? `${hourData.avgValue} Alert/Week` : ''}
          {!isNaN(weekData?.avgValue) ? ` · ${weekData.avgValue} Avg Resp` : ''}
        </span>
        <Link to="/reports/alerts/run" state={{ filters: { areas: [areaId] } }}>
          <button className="dp-btn dp-btn--secondary dp-btn--sm">
            Run Detailed Report &rarr;
          </button>
        </Link>
      </div>

      {/* Charts — separate cards in a responsive grid */}
      <div className="ip-chart-row" style={{ marginBottom: 'var(--space-4)' }}>
        <div className="pp-section">
          <div className="pp-section__header">
            <h3 className="pp-section__title">Alarm Activity by Hour</h3>
          </div>
          <div className="pp-section__body">
            <div className="ip-chart-section">
              <HourlyLineAndBarChart
                style={{ maxHeight: '350px' }}
                labels={hourData?.labels || ['0','1','2','3','4','5','6','7','8','9','10','11','12','13','14','15','16','17','18','19','20','21','22','23']}
                x_axis={true}
                x_axis_title="Time of the Day"
                bar_axis={true}
                bar_axis_title="Total Alarms"
                bar_color="#0081C7"
                line_axis={true}
                line_axis_title="Average Response Time"
                line_color="#BE4D25"
                bar_data={hourData?.events || []}
                line_data={hourData?.responses || []}
                bar_annotation={true}
                line_annotation={true}
                forceAspect={true}
                bar_annotation_value={hourData?.avgValue || 0}
                line_annotation_value={hourData?.avgResp || 0}
              />
            </div>
          </div>
        </div>

        <div className="pp-section">
          <div className="pp-section__header">
            <h3 className="pp-section__title">Weekly Alarm Trend</h3>
          </div>
          <div className="pp-section__body">
            <div className="ip-chart-section">
              <LineTrendingChart
                style={{ maxHeight: '350px' }}
                x_axis={true}
                x_axis_title="Weeks"
                line_axis={true}
                line_axis_title="Total Alarms"
                lines={1}
                line_color={weekChartProps.lineColors}
                line_labels={weekChartProps.lineLabels}
                line_data={weekChartProps.lineData}
                forceAspect={true}
                labels={weekChartProps.axisLabels}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Event Reason Breakdown + Recent Events */}
      <div className="ip-report-grid">
        {/* Event Reason Breakdown */}
        <div className="pp-section">
          <div className="pp-section__header">
            <h3 className="pp-section__title">Event Reason Breakdown</h3>
          </div>
          <div className="pp-section__body pp-section__body--flush">
            <div className="dp-table-wrap">
              <table className="dp-table">
                <thead className="dp-thead">
                  <tr>
                    <th className="dp-th">Reason</th>
                    <th className="dp-th" style={{ textAlign: 'center' }}>Total</th>
                    <th className="dp-th" style={{ textAlign: 'center' }}>% Total Volume</th>
                    <th className="dp-th" style={{ textAlign: 'center' }}>Avg Response (min)</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Summary row */}
                  <tr className="dp-tr ip-reason-total">
                    <td className="dp-td"><span className="dp-cell-text">Total Events</span></td>
                    <td className="dp-td" style={{ textAlign: 'center' }}>
                      <span className="dp-cell-text">{reasonData?.count || 0}</span>
                    </td>
                    <td className="dp-td" style={{ textAlign: 'center' }}></td>
                    <td className="dp-td" style={{ textAlign: 'center' }}>
                      <span className="dp-cell-text">{reasonData?.avg_response_time || '0.0'}</span>
                    </td>
                  </tr>
                  {/* Detail rows */}
                  {reasonRows.map((row, idx) => (
                    <tr key={idx} className="dp-tr">
                      <td className="dp-td"><span className="dp-cell-text">{row.reason_name || '—'}</span></td>
                      <td className="dp-td" style={{ textAlign: 'center' }}>
                        <span className="dp-cell-text">{row.count || 0}</span>
                      </td>
                      <td className="dp-td">
                        <div className="ip-percent-bar">
                          <div className="ip-percent-bar__track">
                            <div
                              className="ip-percent-bar__fill"
                              style={{ width: `${parseFloat(row.percent) || 0}%` }}
                            />
                          </div>
                          <span className="ip-percent-bar__label">{row.percent || '—'}</span>
                        </div>
                      </td>
                      <td className="dp-td" style={{ textAlign: 'center' }}>
                        <span className="dp-cell-text">{row.avg_response_time || '—'}</span>
                      </td>
                    </tr>
                  ))}
                  {reasonRows.length === 0 && (
                    <tr className="dp-tr">
                      <td className="dp-td" colSpan={4} style={{ textAlign: 'center' }}>
                        <span className="dp-cell-text dp-cell-text--muted">No event data for this period</span>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Recent Events */}
        <div className="pp-section">
          <div className="pp-section__header">
            <h3 className="pp-section__title">Recent Events</h3>
          </div>
          <div className="pp-section__body pp-section__body--flush" style={{ maxHeight: '400px', overflowY: 'auto' }}>
            <div className="dp-table-wrap">
              <table className="dp-table">
                <thead className="dp-thead">
                  <tr>
                    <th className="dp-th">Event Start</th>
                    <th className="dp-th" style={{ textAlign: 'center' }}>Resp (min)</th>
                    <th className="dp-th">Device</th>
                    <th className="dp-th">Resident</th>
                    <th className="dp-th" style={{ textAlign: 'center' }}>Reason</th>
                    {eventNotesActive && <th className="dp-th" style={{ textAlign: 'center' }}>Notes</th>}
                  </tr>
                </thead>
                <tbody>
                  {eventData.map((evt, idx) => (
                    <tr key={evt.event_id || idx} className="dp-tr">
                      <td className="dp-td">
                        <Link to={`/profiles/event/${evt.event_id}`} className="pp-field__value--link" style={{ fontSize: 'var(--font-size-sm)' }}>
                          {evt.event_datetime_local ? convertIsoToLocal(evt.event_datetime_local) : '—'}
                        </Link>
                      </td>
                      <td className="dp-td" style={{ textAlign: 'center' }}>
                        <span className="dp-cell-text">{evt.response_time ?? '—'}</span>
                      </td>
                      <td className="dp-td">
                        {evt.device_id ? (
                          <Link to={getDeviceLink(evt)} className="pp-field__value--link" style={{ fontSize: 'var(--font-size-sm)' }}>
                            {evt.description || evt.manufacture_uuid || '—'}
                          </Link>
                        ) : (
                          <span className="dp-cell-text dp-cell-text--muted">—</span>
                        )}
                      </td>
                      <td className="dp-td">
                        {evt.resident_id ? (
                          <Link to={`/community/residents/${evt.resident_id}`} className="pp-field__value--link" style={{ fontSize: 'var(--font-size-sm)' }}>
                            {evt.resident_name || '—'}
                          </Link>
                        ) : (
                          <span className="dp-cell-text dp-cell-text--muted">—</span>
                        )}
                      </td>
                      <td className="dp-td" style={{ textAlign: 'center' }}>
                        <span className="dp-cell-text">
                          {evt.reason_code_description || '—'}
                          {isReasonEditable && evt.reason_edits?.length > 0 && (
                            <span title="This value has been edited" style={{ marginLeft: '4px', color: 'var(--color-warning, #d9a406)' }}>*</span>
                          )}
                        </span>
                      </td>
                      {eventNotesActive && (
                        <td className="dp-td" style={{ textAlign: 'center' }}>
                          <button
                            className="dp-btn dp-btn--secondary dp-btn--xs"
                            onClick={() => onAddNote(idx)}
                            title="Add Note"
                          >
                            {evt.notes?.length > 0 ? `${evt.notes.length}` : '+'} Note{evt.notes?.length !== 1 ? 's' : ''}
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                  {eventData.length === 0 && (
                    <tr className="dp-tr">
                      <td className="dp-td" colSpan={eventNotesActive ? 6 : 5} style={{ textAlign: 'center' }}>
                        <span className="dp-cell-text dp-cell-text--muted">No recent events</span>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ReportingTab;
